/**
 * `NotificationsPanel` — "Emails to you" (founder brief 2026-10-20,
 * consolidated Notifications work).
 *
 * One switch per email TYPE that Sill may send to this signed-in
 * user. Flipping a switch off is a PERSONAL opt-out across every
 * site in the account; the per-SITE Notifications page controls who
 * else receives the emails and whether they're sent at all.
 *
 * Each switch's description sits inside the shared `Tooltip` atom
 * (same `Info size={13}` pattern as `SettingsNotifications`). Below
 * the switches is a short line linking out to each site's
 * Notifications page so a user who wants to tune a single site's
 * emails doesn't have to leave this view to find the surface.
 *
 * Defaults are "on" — the server treats missing / NULL keys as opt-
 * in and only an explicit `false` excludes.
 *
 * The "Sign-in from a new country" switch moved OUT of this panel
 * into Account › Security as part of the consolidation (that email
 * is a security event about this user's own session, not a merchant
 * notification).
 */

import { useCallback, useState } from 'react';
import { Info } from 'lucide-react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card, Tooltip } from '../../components/atoms/index.js';
import { patchMe } from '../../api/account-me.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/**
 * One row per email type. The key is the per-user
 * `notification_preferences` JSONB field; absent → opt-in. New
 * channels (future WhatsApp) do NOT belong here — this surface is
 * per-user opt-outs across all sites, not per-site channel picks.
 */
export type PersonalEmailPrefKey =
  | 'escalation_digest_email'
  | 'agent_weekly_digest_email'
  | 'manifest_change_email'
  | 'security_alerts_email'
  | 'plan_usage_email';

interface PersonalRow {
  readonly key: PersonalEmailPrefKey;
  readonly label: string;
  readonly tooltip: string;
}

const ROWS: readonly PersonalRow[] = [
  {
    key: 'escalation_digest_email',
    label: 'Orders waiting for approval',
    tooltip:
      'The grouped email when an agent order needs human review. Turning '
        + 'this off silences the email for YOU across every site; the site '
        + 'still sends the email to other recipients.',
  },
  {
    key: 'agent_weekly_digest_email',
    label: 'Monday agent email',
    tooltip:
      'The weekly digest: which AI agents visited, what they asked for, and '
        + "what your guardrails refused. One email a week on Monday.",
  },
  {
    key: 'manifest_change_email',
    label: 'Agent skill changes',
    tooltip:
      "An email when an agent that visited one of your sites changes its "
        + 'declared skill set. Review in your dashboard to see the exact hash.',
  },
  {
    key: 'security_alerts_email',
    label: 'Security alerts',
    tooltip:
      'All security emails Sill may send: an attacker got through, a new '
        + 'group of attackers, or the Security section in the Monday email. '
        + 'Turning this off silences every security email for YOU.',
  },
  {
    key: 'plan_usage_email',
    label: 'Plan usage emails',
    tooltip:
      'When a site reaches 80% or 100% of a plan limit.',
  },
];

export interface NotificationsPanelProps {
  /**
   * The entire `notification_preferences` map off the signed-in user
   * row. Missing keys resolve to opt-in on the server (default-on
   * contract); this component only cares about an explicit `false`.
   */
  readonly notification_preferences?: Record<string, boolean> | undefined;
  /** Called after a successful PATCH so the parent can refresh state. */
  readonly onUpdated: (patch: Record<string, boolean>) => void;
  /** Dashboard origin for the per-site deep links. Defaults to window.origin. */
  readonly dashboard_origin?: string | undefined;
  /** The signed-in user's sites. Rendered as deep-link rows under the switches. */
  readonly sites?: readonly { readonly site_id: string; readonly domain: string }[] | undefined;
}

function resolveChecked(
  prefs: Record<string, boolean> | undefined,
  key: PersonalEmailPrefKey,
): boolean {
  return prefs?.[key] !== false;
}

export function NotificationsPanel({
  notification_preferences,
  onUpdated,
  dashboard_origin,
  sites,
}: NotificationsPanelProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const [pendingKey, setPendingKey] = useState<PersonalEmailPrefKey | null>(null);

  const onToggle = useCallback(
    async (key: PersonalEmailPrefKey): Promise<void> => {
      if (http === null || pendingKey !== null) return;
      const previous = resolveChecked(notification_preferences, key);
      const next = !previous;
      setPendingKey(key);
      onUpdated({ [key]: next });
      const result = await patchMe(http, {
        notification_preferences: { [key]: next },
      });
      setPendingKey(null);
      switch (result.kind) {
        case 'ok': {
          const echoed = result.user.notification_preferences?.[key];
          onUpdated({ [key]: echoed !== false });
          toast({
            tone: 'lime',
            title: next ? 'Turned on.' : 'Turned off.',
          });
          break;
        }
        case 'bad_request':
          onUpdated({ [key]: previous });
          toast({ tone: 'coral', title: 'Could not save preference.' });
          break;
        case 'rate_limited':
          onUpdated({ [key]: previous });
          toast({ tone: 'amber', title: result.message });
          break;
        case 'error':
          onUpdated({ [key]: previous });
          toast({
            tone: 'coral',
            title: 'Could not save preference.',
            body: result.message,
          });
          break;
      }
    },
    [http, pendingKey, notification_preferences, onUpdated, toast],
  );

  const origin =
    dashboard_origin !== undefined && dashboard_origin.length > 0
      ? dashboard_origin.replace(/\/$/, '')
      : typeof window !== 'undefined' && window.location.origin.length > 0
        ? window.location.origin.replace(/\/$/, '')
        : '';

  return (
    <section id="notifications">
      <Card className="p-5 sm:p-6">
        <div
          className="mb-5 pb-4"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            NOTIFICATIONS
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.5,
              marginBottom: 4,
            }}
          >
            Emails to you
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            Turn an email type off and it stops for you across every
            site. The site keeps sending the email to other recipients.
            To tune a single site, open that site&apos;s Notifications
            page below.
          </div>
        </div>

        <div className="space-y-3">
          {ROWS.map((row) => {
            const checked = resolveChecked(notification_preferences, row.key);
            const pending = pendingKey === row.key;
            return (
              <div
                key={row.key}
                className="flex items-start justify-between gap-3 p-3"
                style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
              >
                <div
                  className="flex-1"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span>{row.label}</span>
                  <Tooltip content={row.tooltip} side="top">
                    <span
                      aria-label={`About ${row.label}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: C.muted,
                        cursor: 'help',
                      }}
                    >
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  aria-label={row.label}
                  disabled={pending}
                  onClick={() => void onToggle(row.key)}
                  style={{
                    width: 44,
                    height: 26,
                    minWidth: 44,
                    borderRadius: 13,
                    border: `1px solid ${checked ? C.lime : C.border}`,
                    background: checked ? C.lime : C.surface2,
                    position: 'relative',
                    cursor: pending ? 'not-allowed' : 'pointer',
                    transition: 'background 120ms, border-color 120ms',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: checked ? 20 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: checked ? C.ink : C.muted,
                      transition: 'left 120ms',
                    }}
                  />
                </button>
              </div>
            );
          })}
        </div>

        {sites !== undefined && sites.length > 0 ? (
          <div
            data-testid="per-site-notification-links"
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 2,
                marginBottom: 10,
              }}
            >
              PER-SITE SETTINGS
            </div>
            <div className="space-y-1">
              {sites.map((s) => (
                <a
                  key={s.site_id}
                  href={`${origin}/?view=settings&settings_tab=notifications&site=${encodeURIComponent(s.site_id)}`}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    textDecoration: 'underline',
                    display: 'block',
                    padding: '4px 0',
                  }}
                >
                  Notifications for {s.domain}
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </Card>
    </section>
  );
}
